import { getNodesBounds, type Node } from "@xyflow/react";

export const WORKSPACE_CANVAS_VIEWPORT_LEFT_PADDING = 32;
export const WORKSPACE_CANVAS_VIEWPORT_RIGHT_PADDING = 48;
/** Extra top inset so fit-view clears the floating workspace toolbar. */
export const WORKSPACE_CANVAS_VIEWPORT_TOP_PADDING = 88;
export const WORKSPACE_CANVAS_VIEWPORT_BOTTOM_PADDING = 48;
/** Bottom inset when the strategy pine dock is open over the canvas. */
export const WORKSPACE_CANVAS_VIEWPORT_PINE_DOCK_BOTTOM_PADDING = 340;
/** @deprecated Prefer TOP/BOTTOM — kept for older imports. */
export const WORKSPACE_CANVAS_VIEWPORT_VERTICAL_PADDING = WORKSPACE_CANVAS_VIEWPORT_BOTTOM_PADDING;
export const WORKSPACE_CANVAS_DEFAULT_MAX_ZOOM = 1;
export const WORKSPACE_CANVAS_ZOOM_FACTOR = 1.2;
export const WORKSPACE_CANVAS_MIN_ZOOM = 0.15;
export const WORKSPACE_CANVAS_MAX_ZOOM = 1.75;

export function getWorkspaceGraphCenter<
  NodeData extends Record<string, unknown> = Record<string, unknown>,
>(nodes: Array<Node<NodeData>>): { x: number; y: number } {
  const bounds = getNodesBounds(nodes);
  return {
    x: bounds.x + bounds.width / 2,
    y: bounds.y + bounds.height / 2,
  };
}

/** Zoom while keeping a flow-space point fixed on screen (graph stays the zoom focal point). */
export function zoomViewportAtFlowPoint(
  viewport: WorkspaceCanvasViewport,
  flowPoint: { x: number; y: number },
  newZoom: number
): WorkspaceCanvasViewport {
  const screenX = flowPoint.x * viewport.zoom + viewport.x;
  const screenY = flowPoint.y * viewport.zoom + viewport.y;

  return {
    zoom: newZoom,
    x: screenX - flowPoint.x * newZoom,
    y: screenY - flowPoint.y * newZoom,
  };
}

export function zoomWorkspaceViewportAtGraph(
  viewport: WorkspaceCanvasViewport,
  graphCenter: { x: number; y: number },
  direction: "in" | "out",
  limits: { minZoom: number; maxZoom: number } = {
    minZoom: WORKSPACE_CANVAS_MIN_ZOOM,
    maxZoom: WORKSPACE_CANVAS_MAX_ZOOM,
  }
): WorkspaceCanvasViewport {
  const factor = direction === "in" ? WORKSPACE_CANVAS_ZOOM_FACTOR : 1 / WORKSPACE_CANVAS_ZOOM_FACTOR;
  const newZoom = Math.min(limits.maxZoom, Math.max(limits.minZoom, viewport.zoom * factor));
  return zoomViewportAtFlowPoint(viewport, graphCenter, newZoom);
}

type ViewportRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type WorkspaceCanvasViewport = {
  x: number;
  y: number;
  zoom: number;
};

/** Frame graph from the left edge, vertically centered in the usable canvas band. */
export function computeWorkspaceCanvasViewport(
  bounds: ViewportRect,
  canvasWidth: number,
  canvasHeight: number,
  options?: { maxZoom?: number; bottomPadding?: number }
): WorkspaceCanvasViewport {
  if (canvasWidth <= 0 || canvasHeight <= 0 || bounds.width <= 0 || bounds.height <= 0) {
    return { x: 0, y: 0, zoom: 1 };
  }

  const maxZoom = options?.maxZoom ?? WORKSPACE_CANVAS_DEFAULT_MAX_ZOOM;
  const bottomPadding = options?.bottomPadding ?? WORKSPACE_CANVAS_VIEWPORT_BOTTOM_PADDING;
  const availableWidth =
    canvasWidth - WORKSPACE_CANVAS_VIEWPORT_LEFT_PADDING - WORKSPACE_CANVAS_VIEWPORT_RIGHT_PADDING;
  const availableHeight =
    canvasHeight - WORKSPACE_CANVAS_VIEWPORT_TOP_PADDING - bottomPadding;

  if (availableWidth <= 0 || availableHeight <= 0) {
    return { x: 0, y: 0, zoom: 1 };
  }

  const widthZoom = availableWidth / bounds.width;
  const heightZoom = availableHeight / bounds.height;
  const zoom = Math.min(maxZoom, widthZoom, heightZoom);

  const x = WORKSPACE_CANVAS_VIEWPORT_LEFT_PADDING - bounds.x * zoom;
  const centerY = bounds.y + bounds.height / 2;
  const y =
    WORKSPACE_CANVAS_VIEWPORT_TOP_PADDING + availableHeight / 2 - centerY * zoom;

  return { x, y, zoom };
}

export function computeWorkspaceCanvasViewportForNodes<
  NodeData extends Record<string, unknown> = Record<string, unknown>,
>(
  nodes: Array<Node<NodeData>>,
  canvasWidth: number,
  canvasHeight: number,
  options?: { maxZoom?: number; bottomPadding?: number }
): WorkspaceCanvasViewport {
  if (nodes.length === 0) {
    return { x: 0, y: 0, zoom: 1 };
  }

  const bounds = getNodesBounds(nodes);
  return computeWorkspaceCanvasViewport(bounds, canvasWidth, canvasHeight, options);
}
